import Box from "@mui/material/Box";
import { Autocomplete, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, IconButton, Paper, TextField, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import {
    useDeletePublicReportMutation,
    useGetPublicReportsQuery,
} from "../../../services/report.query";
import { DefaultPage } from "../../../layouts/default-page";
import { Add, Delete, Edit, Preview } from "@mui/icons-material";
import { DataTable } from "../../../layouts/data-table";
import { format } from "date-fns";
import DeleteConfirmationDialog from "../../../layouts/delete-confirm";
import { useGetDeputyReviewsQuery } from "../../../services/deputy-reviews.query";
import { useNavigate, useLocation } from "react-router-dom";
import { useAddBoardMemberCommitteeMutation, useDeleteBoardMemberCommitteeMutation, useGetBoardMembersQuery, useGetCommitteeMembersQuery, useGetCommitteesQuery } from "../../../services/appointees.query";
import * as Yup from "yup";
import { Field, useFormik } from "formik";
import { Notify } from "../../../layouts/notify";
import { LoadingButton } from "@mui/lab";

interface LoadingState {
    [id: string]: boolean; // id as the key and a boolean indicating loading state
}

const addMemberValidationSchema = Yup.object({
    board_member_id: Yup.string().required("Board Member is required"),
    role: Yup.string().required("Role is required"),
});

export default function CommitteeMembers() {
    const location = useLocation();
    const committeeData = location.state?.committeeData;
    const [openAddModal, setOpenAddModal] = useState<boolean>(false);
    const [addBoardMemberCommittee, { isLoading: addBoardMemberCommitteeLoading }] = useAddBoardMemberCommitteeMutation();
    const [deleteBoardMemberCommittee, { isLoading: deleteLoading }] = useDeleteBoardMemberCommitteeMutation();
    const { data: boardMembers } = useGetBoardMembersQuery("");
    const [open, setOpen] = React.useState<boolean>(false);
    const [selectedItem, setSelectedItem] = React.useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState("");
    const [loadingState, setLoadingState] = useState<LoadingState>({});

    const handleCloseAddModal = () => {

        setOpenAddModal(false);
    };

    const handleOpenAddModal = () => {
        addFormik.resetForm();
        setOpenAddModal(true);
    };

    const {
        data: Committees,
        isLoading,
        isFetching,
        isSuccess,
        isError,
    } = useGetCommitteeMembersQuery(committeeData.id);

    const addFormik = useFormik({
        initialValues: {
            board_member_id: "",
            board_committee_id: committeeData.id,
            is_active: true, // Initialize is_active state
            role: ""
        },
        validationSchema: addMemberValidationSchema,
        onSubmit: async (values: any) => {
            try {
                await addBoardMemberCommittee({
                    ...values,
                });
                Notify("info", "Committee added successfully");
                setOpenAddModal(false);
            } catch (error) {
                console.error("Error during form submission:", error);
                Notify("error", "Failed to add committee, " + error);
            }
        },
    });

    const handleDelete = async (id: string) => {
        setLoadingState((prev) => ({ ...prev, [id]: true }));
        try {

            await deleteBoardMemberCommittee(id);
            Notify("info", "Committee member deleted successfully");
        } catch (error) {
            console.error("Error during delete operation:", error);
            Notify("error", "Failed to delete committee member, "+ error);
        }
        setLoadingState((prev) => ({ ...prev, [id]: false }));
    };


    const columns = [
        {
            accessorKey: "profile_url",
            header: "Member",
            Cell: ({ row }: any) => {
                return (
                    <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <img
                                src={row.original.member.profile_url}
                                style={{ width: 35, height: 35, cursor: "pointer" }}
                                onClick={() => window.open(row.original.member.profile_url, "_blank")}
                            />
                            <Typography > {row.original.member.first_name} {row.original.member.middle_initial} {row.original.member.last_name}</Typography>
                        </Box>{" "}
                    </>
                );
            },
        },

        {
            accessorKey: "role",
            header: "Role",
            size: 50,
        },
        {
            accessorKey: "is_active",
            header: "Status",
            size: 15,
            Cell: ({ row }: any) => {
                const status = row.original.is_active;
                let val;
                let color;
                if (status == true) {
                    color = "#0020ed";
                    val = "ACTIVE"
                } else {
                    color = "red"; // default color
                    val = "INACTIVE"
                }

                return (
                    <Typography style={{ color: color }}>
                        {val}
                    </Typography>
                );
            },
        }

    ];

    // Filtered data using global search across multiple columns
    const filteredData = useMemo(() => {
        if (!globalFilter) return Committees; // return all movies if no filter is applied
        return Committees?.filter(
            (pr: { name: any; }) => {
                const searchContent =
                    `${pr?.name}`.toLowerCase();
                return searchContent?.includes(globalFilter.toLowerCase());
            }
        );
    }, [globalFilter, Committees]);

    const navigate = useNavigate();

    const viewReview = (rowData: any) => {
        navigate("/deputies/details", { state: { deputyReview: rowData } });
    };

    return (
        <DefaultPage
            title={committeeData.name}
            primaryButtonProps={{
                startIcon: <Add />,
                children: "Add New",
                onClick: () => handleOpenAddModal(),
            }}
        >
            <DataTable
                count={Committees?.length}
                columns={columns}
                globalFilter={setGlobalFilter}
                list={filteredData as any}
                getRowId={(row: { id: any }) => row.id}
                isLoading={isLoading}
                isSuccess={isSuccess}
                toolbar={true}
                toolbarActions={true}
                isFetching={isFetching}
                enableBottomToolbar={true}
                enablePinning={true}
                isError={isError}
                toolbarActionMargin="2"
                renderRowActions={({ row }: any) => (
                    <Box sx={{ display: "flex" }}>
                        <Button
                            color="error"
                            onClick={() => handleDelete(row?.original.id)}
                            disabled={loadingState[row?.original.id]} // Disable only the button for the loading item
                        >
                            {loadingState[row?.original.id] ? ( // Check if this specific item is loading
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                <Delete />
                            )}
                        </Button>
                    </Box>
                )}
            />
            <Dialog
                open={openAddModal}
                onClose={handleCloseAddModal}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="sm" // Set the dialog to medium width
                fullWidth // Make the dialog full width
            >
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: "white",
                        width: "100%",
                        borderRadius: "15px",
                        padding: "15px",
                    }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseAddModal}
                        sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <img alt={"close icon"} src={"/x.png"} />
                    </IconButton>
                    <Typography variant="subtitle1" component="div">
                        Add Committee
                    </Typography>
                    <form noValidate onSubmit={addFormik.handleSubmit}>
                        <DialogContent sx={{ width: "100%", height: "100%" }}>
                            <FormControl fullWidth>
                                <label
                                    htmlFor="board_member_id"
                                    style={{
                                        color: "#AFAFAF",
                                        marginBottom: "7px",
                                    }}
                                >
                                    Board Member
                                </label>
                                <Autocomplete
                                    options={boardMembers || []}
                                    getOptionLabel={(boardMember: any) => (boardMember.first_name + ' ' + boardMember.middle_initial + ' ' + boardMember.last_name)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <img
                                                    src={option.profile_url}
                                                    alt={option.name}
                                                    style={{ width: 35, height: 35, borderRadius: '50%' }}
                                                />
                                                <Typography>{(option.first_name + ' ' + option.middle_initial + ' ' + option.last_name)}</Typography>
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Board Members"
                                            variant="outlined"
                                            error={addFormik.touched.board_member_id && Boolean(addFormik.errors.board_member_id)}
                                            helperText={
                                                addFormik.touched.board_member_id && typeof addFormik.errors.board_member_id === "string"
                                                    ? addFormik.errors.board_member_id
                                                    : ""
                                            }
                                        />
                                    )}
                                    onChange={(event, value) => {
                                        addFormik.setFieldValue('board_member_id', value ? value.id : '');
                                    }}
                                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                                />
                            </FormControl>

                            {/* New Dropdown for Role Selection */}
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <label
                                    htmlFor="role"
                                    style={{
                                        color: "#AFAFAF",
                                        marginBottom: "7px",
                                    }}
                                >
                                    Role
                                </label>
                                <Autocomplete
                                    options={["Chairperson", "Co-chairperson", "Member"]}
                                    getOptionLabel={(role) => role}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Role"
                                            variant="outlined"
                                            error={addFormik.touched.role && Boolean(addFormik.errors.role)}
                                            helperText={
                                                addFormik.touched.role && typeof addFormik.errors.role === "string"
                                                    ? addFormik.errors.role
                                                    : ""
                                            }
                                        />
                                    )}
                                    onChange={(event, value) => {
                                        addFormik.setFieldValue('role', value || '');
                                    }}
                                />
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Grid container spacing={2}>
                                <Grid item xs={1} sm={1} md={1} />
                                <Grid item xs={10} sm={10} md={10}>
                                    <LoadingButton
                                        color="primary"
                                        type={"submit"}
                                        sx={{
                                            textTransform: "none",
                                            color: "white",
                                            width: "100%",
                                            borderRadius: "20px",
                                            "&:hover": {
                                                backgroundColor: "#5489de",
                                                color: "white",
                                            },
                                            margin: 1,
                                            backgroundColor: "#0054D3",
                                        }}
                                        loading={
                                            addBoardMemberCommitteeLoading || addFormik.isSubmitting
                                        }
                                    >
                                        Save
                                    </LoadingButton>
                                </Grid>
                                <Grid item xs={1} sm={1} md={1} />
                            </Grid>
                        </DialogActions>
                    </form>
                </Paper>
            </Dialog>
        </DefaultPage>
    );
}
